import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./NotificationBell.module.scss";
import classnames from "classnames";
import Menu from "@src/components/common/Menu";
import MenuItem from "@src/components/common/Menu/MenuItem";
import NumberBubble from "@src/components/common/NumberBubble";
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
} from "@src/api/NotificationApi";
import type { Notification, PaginatedResponse } from "@src/types/notification";

interface NotificationBellProps {
  className?: string;
}

// Icon type to Font Awesome icon mapping
const ICON_MAP: Record<string, string> = {
  trophy: "fa-trophy",
  "truck-fast": "fa-truck-fast",
  "boxes-stacked": "fa-boxes-stacked",
  "thumbs-up": "fa-thumbs-up",
  "box-heart": "fa-box-heart",
  bell: "fa-bell",
};

// Icon type to color mapping (matching mockup)
const ICON_COLOR_MAP: Record<string, string> = {
  trophy: "var(--notification-yellow)",
  "truck-fast": "var(--notification-green)",
  "boxes-stacked": "var(--notification-purple)",
  "thumbs-up": "var(--notification-blue)",
  "box-heart": "var(--notification-pink)",
  bell: "var(--notification-gray)",
};

export default function NotificationBell({ className }: NotificationBellProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const navigate = useNavigate();

  // Fetch notifications and unread count on mount
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await getNotifications({
        isRead: false,
        page: 1,
        pageSize: 1, // We only need the count, not the actual results
      });
      const data: PaginatedResponse<Notification> = response.body;
      setUnreadCount(data.count);
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  };

  // Handle dropdown open/close - fetch fresh notifications when opened
  const handleMenuOpenChanged = useCallback(async (isOpen: boolean) => {
    if (!isOpen) {
      return;
    }
    await fetchNotifications(1);
  }, []);

  const handleClearAll = async () => {
    try {
      await markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const fetchNotifications = async (page: number = 1) => {
    setIsLoading(true);
    try {
      const response = await getNotifications({
        // Fetch both read and unread notifications
        page,
        pageSize: 10,
      });

      const data: PaginatedResponse<Notification> = response.body;

      if (page === 1) {
        // First page - replace notifications
        setNotifications(data.results);
      } else {
        // Subsequent pages - append to existing notifications
        setNotifications((prev) => [...prev, ...data.results]);
      }

      setNextPage(data.next);
      setCurrentPage(page);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Navigate to link URL if provided
    if (notification.linkUrl) {
      navigate(notification.linkUrl);
    }

    // Mark as read if not already read
    if (!notification.isRead) {
      try {
        await markAsRead(notification.id);

        // DON'T update local state - let it stay visually unread until next dropdown open
        // The backend has marked it as read

        // Refresh unread count from server to update badge
        await fetchUnreadCount();
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }
  };

  const handleShowMore = () => {
    if (nextPage && !isLoading) {
      fetchNotifications(currentPage + 1);
    }
  };

  const getIconClass = (iconType?: string): string => {
    if (!iconType) return ICON_MAP.bell;
    return ICON_MAP[iconType] || ICON_MAP.bell;
  };

  const getIconColor = (iconType?: string): string => {
    if (!iconType) return ICON_COLOR_MAP.bell;
    return ICON_COLOR_MAP[iconType] || ICON_COLOR_MAP.bell;
  };

  return (
    <div className={classnames(styles.root, className)}>
      <Menu
        buttonContent={
          <div
            id="page-header-notification-bell-button"
            data-testid="page-header-notification-bell-button"
            className={styles.bellButton}
          >
            <i className="fa-light fa-bell" />
            {unreadCount > 0 && (
              <span className={styles.badge}>
                <NumberBubble value={unreadCount} />
              </span>
            )}
          </div>
        }
        onOpenChanged={handleMenuOpenChanged}
      >
        <div className={styles.notificationDropdown}>
          <div className={styles.header}>
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={handleClearAll} className="btn-link">
                Clear All
              </button>
            )}
          </div>

          <div className={styles.notificationList}>
            {isLoading && notifications.length === 0 ? (
              <div className={styles.emptyState}>
                <i className="fa-light fa-spinner fa-spin" />
                <p>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className={styles.emptyState}>
                <i className="fa-light fa-bell" />
                <p>No new notifications</p>
              </div>
            ) : (
              <>
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    data-testid={`notification-row-${notification.id}`}
                    className={classnames(styles.notificationItem, {
                      [styles.read]: notification.isRead,
                    })}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div
                      className={styles.iconCircle}
                      style={{
                        backgroundColor: getIconColor(notification.iconType),
                      }}
                    >
                      <i
                        className={classnames(
                          "fa-solid",
                          getIconClass(notification.iconType),
                        )}
                      />
                    </div>
                    <div className={styles.content}>
                      <div className={styles.title}>{notification.title}</div>
                      {notification.linkUrl && (
                        <div className={styles.link}>
                          {notification.actionText || "View Details"}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {nextPage && (
                  <div className={styles.showMore}>
                    <button
                      onClick={handleShowMore}
                      disabled={isLoading}
                      className={styles.showMoreButton}
                      data-testid="notification-show-more-button"
                    >
                      {isLoading ? (
                        <>
                          <i className="fa-light fa-spinner fa-spin" />
                          Loading...
                        </>
                      ) : (
                        "Show More"
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Menu>
    </div>
  );
}
